import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Import icons
import { FiCheck } from 'react-icons/fi';
import { FiCalendar } from 'react-icons/fi';
import { FiMail } from 'react-icons/fi';
import { FiUsers } from 'react-icons/fi';
import { FiArrowRight } from 'react-icons/fi';
import { FiX } from 'react-icons/fi';
import { FiClock } from 'react-icons/fi';
import { FiAlertCircle } from 'react-icons/fi';
import { FiBriefcase } from 'react-icons/fi';

// --- Animation Variants ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { type: 'spring', stiffness: 100 }
  },
};

const importantItemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    scale: [1, 1.02, 1],
    transition: {
      type: 'spring',
      stiffness: 100,
      scale: {
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut',
      },
    },
  },
};

function App() {
  const [showPopup, setShowPopup] = useState(false);
  const [nextWorkshopDate, setNextWorkshopDate] = useState('');

  const getNextSundayWorkshop = () => {
    const now = new Date();
    const currentDay = now.getDay(); 
    const currentHour = now.getHours();
    
    let nextSunday = new Date();
    
    if (currentDay === 0 && currentHour < 10) {
      // Today is the workshop day
    } 
    else {
      const daysUntilNextSunday = currentDay === 0 ? 7 : (7 - currentDay);
      nextSunday.setDate(now.getDate() + daysUntilNextSunday);
    }
    
    nextSunday.setHours(10, 0, 0, 0);
    return nextSunday;
  };

  const formatWorkshopDate = (date) => {
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Asia/Kolkata'
    };
    return date.toLocaleDateString('en-US', options);
  };

  const getTimeUntilWorkshop = (workshopDate) => {
    const now = new Date();
    const timeDiff = workshopDate.getTime() - now.getTime();
    
    if (timeDiff <= 0) {
      return "Workshop is happening now!";
    }
    
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} and ${hours} hour${hours !== 1 ? 's' : ''} to go!`;
    } else {
      return `${hours} hour${hours !== 1 ? 's' : ''} to go!`;
    }
  };

  const handleWhatsAppClick = () => {
    window.open('https://chat.whatsapp.com/GWMhCNCoUGhFuBkqFUUMhL', '_blank');
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  useEffect(() => {
    const updateWorkshopDate = () => {
      const nextWorkshop = getNextSundayWorkshop();
      setNextWorkshopDate(formatWorkshopDate(nextWorkshop));
    };

    updateWorkshopDate();
    const interval = setInterval(updateWorkshopDate, 60000);

    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 10000);
    
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, []);

  const nextWorkshop = getNextSundayWorkshop();
  const timeUntilWorkshop = getTimeUntilWorkshop(nextWorkshop);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 font-['Inter']">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-center">
            <h1 className="text-2xl font-bold text-gray-800">10XAI Founder Workshop</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Main Congratulations Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="bg-green-100 border-2 border-green-300 rounded-2xl p-8 mb-8 shadow-sm">
            <div className="flex justify-center mb-4">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 150, damping: 15, delay: 0.2 }}
                className="bg-green-500 rounded-full p-4"
              >
                <FiCheck className="text-4xl text-white" />
              </motion.div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-green-700 mb-4">
              Congratulations! 🎉
            </h1>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
              Your Seat is Reserved!
            </h2>
            <div className="bg-white/50 rounded-xl p-4 border border-green-200 inline-block mb-6">
              <p className="text-lg font-semibold text-green-800 flex items-center justify-center">
                <FiBriefcase className="mr-2" />
                A warm welcome to all Healers joining us! 💼
              </p>
            </div>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
              You've successfully secured your spot. Get ready to learn how to automate your professional workflows, reclaim 20+ hours a week, and lead the AI revolution in your industry! 🚀
            </p>
          </div>
        </motion.div>

        {/* Status Confirmation */}
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-6 mb-8 border-l-4 border-green-500"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-800">Free Workshop Registration Confirmed ✅</h3>
            </div>
            <div className="text-green-500 text-3xl">
              <FiCheck />
            </div>
          </div>
        </motion.div>

        {/* Next Steps Section */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="bg-white rounded-xl shadow-lg p-8 mb-8"
        >
          <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            Next Steps 📋
          </h3>
          
          <div className="space-y-6">
            {/* Step 1 */}
            <motion.div variants={itemVariants} className="flex items-start space-x-4 p-5 bg-blue-50 rounded-xl border border-blue-100">
              <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex-shrink-0 flex items-center justify-center font-bold">1</div>
              <div>
                <h4 className="text-lg font-semibold text-gray-800 flex items-center">
                  <FiMail className="mr-2" />
                  Check Your Email 📧
                </h4>
                <p className="text-gray-600 mt-2">
                  A confirmation email with the workshop details has been sent to your registered email address.
                </p>
                <div className="mt-3 flex items-start space-x-2 text-sm bg-blue-100/50 p-3 rounded-lg border border-blue-200">
                  <FiAlertCircle className="text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-blue-800 font-medium">
                    Note: If you don't see it in your <span className="font-bold">Inbox</span>, please check your <span className="font-bold">Promotions</span> or <span className="font-bold">Spam</span> folders. Mark it as "Not Spam" to ensure you receive the session link!
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Step 2 */}
            <motion.div variants={itemVariants} className="flex items-start space-x-4 p-5 bg-purple-50 rounded-xl border border-purple-100">
              <div className="bg-purple-500 text-white rounded-full w-8 h-8 flex-shrink-0 flex items-center justify-center font-bold">2</div>
              <div className="flex-1">
                <h4 className="text-lg font-semibold text-gray-800 flex items-center">
                  <FiCalendar className="mr-2" />
                  Mark Your Calendar 📅
                </h4>
                <div className="mt-3 p-4 bg-white rounded-lg border border-purple-200 shadow-sm">
                  <p className="text-purple-800 font-bold text-lg">
                    📅 Date: {nextWorkshopDate}
                  </p>
                  <p className="text-purple-700 font-semibold">
                    ⏰ Time: 10:00 AM IST (Sun)
                  </p>
                  <div className="mt-2 flex items-center text-purple-600 text-sm font-medium">
                    <FiClock className="mr-1.5" />
                    <span>{timeUntilWorkshop}</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Step 3 */}
            <motion.div variants={importantItemVariants} className="flex items-start space-x-4 p-5 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border-2 border-green-200 shadow-sm">
              <div className="bg-green-600 text-white rounded-full w-8 h-8 flex-shrink-0 flex items-center justify-center font-bold shadow-md">3</div>
              <div className="flex-1">
                <h4 className="text-lg font-semibold text-gray-800 flex items-center">
                  <FiUsers className="mr-2" />
                  Join the Professional Community 🔥
                </h4>
                <p className="text-gray-600 mt-2 font-medium">
                  This is the most critical step! Join the WhatsApp group for direct access to the workshop links and exclusive AI tools tailored for professionals.
                </p>
                
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleWhatsAppClick}
                  className="mt-4 bg-green-600 text-white px-6 py-3 rounded-lg font-bold flex items-center justify-center space-x-2 shadow-lg hover:bg-green-700 transition-all w-full md:w-auto"
                >
                  <FiUsers className="text-lg" />
                  <span>Join WhatsApp Community Now</span>
                  <FiArrowRight />
                </motion.button>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Main CTA Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-xl p-8 text-white mb-12 relative overflow-hidden"
        >
          <div className="relative z-10 text-center">
            <h3 className="text-3xl font-bold mb-4">
              Don't Miss the Live Link!
            </h3>
            <p className="text-lg text-green-50 mb-8 max-w-2xl mx-auto">
              The workshop link is shared exclusively inside our community 15 minutes before we start. Join 500+ professionals already waiting!
            </p>
            
            <motion.button
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleWhatsAppClick}
              className="bg-white text-green-600 font-extrabold text-xl px-10 py-5 rounded-full shadow-2xl flex items-center justify-center mx-auto space-x-3"
            >
              <span>🚀 Secure My Access on WhatsApp</span>
              <FiArrowRight />
            </motion.button>
          </div>
          
          <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-64 h-64 bg-black/10 rounded-full blur-3xl"></div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1 }}
          className="border-t pt-8 mt-12 text-center text-gray-500"
        >
          <p className="mb-3">
            Need help? Reach out at:
            <a href="mailto:support@yinguru.com" className="text-blue-600 font-medium hover:underline ml-1">
              support@yinguru.com
            </a>
          </p>
          <p className="text-sm">
            © 2026 10XAI Founder Workshop. Empowering professionals through automation.
          </p>
        </motion.div>
      </div>

      {/* Persistence Popup */}
      <AnimatePresence>
        {showPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative"
            >
              <button
                onClick={closePopup}
                className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX size={24} />
              </button>
              
              <div className="text-center">
                <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 shadow-inner">
                  <FiUsers className="text-4xl text-green-600" />
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  One Last Thing! 📢
                </h3>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  The workshop link and exclusive AI checklists are shared <strong>only inside the WhatsApp community</strong>. Make sure you're in!
                </p>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    handleWhatsAppClick();
                    closePopup();
                  }}
                  className="bg-green-600 text-white font-bold text-lg px-8 py-4 rounded-2xl w-full shadow-lg hover:bg-green-700 transition-all flex items-center justify-center space-x-2"
                >
                  <span>Yes, Let Me In! 🚀</span>
                </motion.button>
                
                <button
                  onClick={closePopup}
                  className="text-gray-400 hover:text-gray-600 mt-5 text-sm font-medium transition-colors underline"
                >
                  I'll take my chances
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;